import { useTranslation } from "react-i18next";
import { RiNotificationOffLine } from "react-icons/ri";
import { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  getNotifications,
  readNotification,
  getNotificationsCount,
  setShowNotification,
} from "../../actions/notification";
import { getNotificationParams } from "./helper";
import NotificationCard from "./NotificationCard";
import ActionCards from "./ActionCard";
import { successReadNotificationToast } from "../../actions/bcfData";
import { RootState } from "@Reducers/index";
import ExportedFiles from "./exportedFiles";
import IconComponent from "@CubeComponents/icons";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@Services/axiosWrapper";
import { formCategories } from "@Helpers";
import type { GetAllExportDataResponse } from "@ServerTypes/tasks/tasks.schema";

function Notification(props: any) {
  const { t } = useTranslation();
  const showNotification = useSelector((state: RootState) => state.showNotification);
  const unreadNotificationsCount = useSelector((state: any) => state.unreadNotificationsCount);
  const { customModules, layout } = useSelector((state: any) => state);
  const dispatch = useDispatch();

  const [fetchedData, setFetchedData] = useState<any>({});
  const [newNotifications, setNewNotifications] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState("allNotifications");
  const [previousNotifications, setPreviousNotifications] = useState<any[]>([]);
  const notificationStatus = useRef(showNotification);
  const [takeActionCount, setTakeActionCount] = useState(0);
  const [exportedFilesCount, setExportedFilesCount] = useState(0);

  const handleTakeActionCountChange = useCallback((count: number) => {
    setTakeActionCount((prev) => Math.max(prev, count));
  }, []);

  const handleExportedFilesCountChange = useCallback((count: number) => {
    setExportedFilesCount((prev) => Math.max(prev, count));
  }, []);

  const handleClick = (e: any) => {
    if (!e.target.closest(".preventNotificationClose") && notificationStatus.current) {
      closeNotificationModal();
    }
  };

  const getTabCount = (tabId: string) => {
    switch (tabId) {
      case "allNotifications":
        return unreadNotificationsCount?.count || 0;
      case "takeAction":
        return takeActionCount;
      case "exportedFiles":
        return exportedFilesCount;
      default:
        return 0;
    }
  };

  const allTabs = [
    {
      name: t("Notifications"),
      id: "allNotifications",
      redirectUrl: "/notifications?section=allNotifications",
    },
    {
      name: t("Take Action"),
      id: "takeAction",
      redirectUrl: "/notifications?section=takeAction&module=reviews",
    },
    // {
    //   name: t("Escrow"),
    //   id: "escrow",
    //   redirectUrl: "/notifications?section=escrow",
    // },
    // {
    //   name: t("Support"),
    //   id: "support",
    //   redirectUrl: "/notifications?section=support",
    // },
    {
      name: t("Exported Files"),
      id: "exportedFiles",
      redirectUrl: "/notifications?section=exportedFiles",
    },
  ];

  const closeNotificationModal = () => {
    dispatch(setShowNotification(false));
  };

  const dismissNotification = async (id: any) => {
    const response = await readNotification([id]);
    if (response) {
      const countPreviousNotificationsRequired = Math.max(
        previousNotifications.length >= 4 && previousNotifications.length <= 7
          ? previousNotifications.length + 1
          : previousNotifications.length,
        2,
      );
      const fetchedNotificationsData = await getNotifications(5, countPreviousNotificationsRequired);
      setFetchedData(fetchedNotificationsData);
      dispatch(getNotificationsCount({ read: "NOTIFICATION_UNREAD" }));
      dispatch(successReadNotificationToast([id]));
    }
  };

  const handleNotifications = async () => {
    if (showNotification) {
      const countPreviousNotificationsRequired = Math.max(previousNotifications.length, 2);
      const fetchedNotificationsData = await getNotifications(5, countPreviousNotificationsRequired);
      setFetchedData(fetchedNotificationsData);
    }
  };

  useEffect(() => {
    handleNotifications();
  }, [showNotification]);

  useEffect(() => {
    notificationStatus.current = showNotification;
  }, [showNotification]);

  useEffect(() => {
    window.addEventListener("click", handleClick);
    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, []);

  useEffect(() => {
    const newNotificationsData = fetchedData.new;
    const previousNotificationsData = fetchedData.previous;

    if (newNotificationsData) {
      const newData = newNotificationsData.result.map((notification: any) => getNotificationParams(notification));
      setNewNotifications(newData);
    }

    if (previousNotificationsData) {
      const oldData = previousNotificationsData.result.map((notification: any) => getNotificationParams(notification));
      setPreviousNotifications(oldData);
    }
  }, [fetchedData]);

  useEffect(() => {
    dispatch(getNotificationsCount({ read: "NOTIFICATION_UNREAD" }));
  }, []);

  useEffect(() => {
    const fetchExportedFilesCount = async () => {
      try {
        const response = await axiosWrapper.post<GetAllExportDataResponse>(
          `${getBackendUrl()}/tasks/getAllExportData`,
          {
            value: { skip: 0, take: 0 },
          },
        );
        const count = response?.data?.count || 0;
        setExportedFilesCount(count);
      } catch (error) {
        console.error(error);
        throw error;
      }
    };

    const fetchTakeActionCount = async () => {
      try {
        const response = await axiosWrapper.post<{ status: boolean; data: any }>(
          `${getBackendUrl()}/projects/getActionableCount`,
        );
        if (response?.data?.status) {
          const moduleCount = response.data.data;
          let total = 0;
          if (layout?.WORKFLOW?.ENABLE) {
            total += moduleCount.reviews?.count || 0;
          }
          if (layout?.INSPECTION?.ENABLE) {
            total += moduleCount[formCategories.inspection]?.count || 0;
          }
          if (layout?.PROGRESS_UPDATES?.ENABLE) {
            total += moduleCount[formCategories.progressUpdates]?.count || 0;
          }
          customModules?.forEach((module: any) => {
            total += moduleCount[module.resourceType]?.count || 0;
          });
          setTakeActionCount(total);
        }
      } catch (error) {
        console.error(error);
        throw error;
      }
    };

    if (showNotification) {
      fetchExportedFilesCount();
      fetchTakeActionCount();
    }
  }, [showNotification, layout, customModules]);

  return (
    <>
      {showNotification && (
        <div className="shadow-[0_40px_40px_rgba(106,168,177,0.2)] top-14 end-28 bg-white absolute max-h-[80vh] w-2/5 z-30 border border-[#ecf3fe] overflow-y-auto rounded-2xl preventNotificationClose">
          {/* Header + Tab Section */}
          <div className="sticky top-0 z-10 bg-white">
            <div className="px-6 pt-4 pb-2 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">{t("My Dashboard")}</h2>
              <Link
                to={allTabs.find((tab) => tab.id === activeTab)?.redirectUrl || "/notifications"}
                onClick={closeNotificationModal}
              >
                <IconComponent name="Redirect" className="w-5 h-5" />
              </Link>
            </div>
            <div className="flex border-b border-gray-200 px-6">
              {allTabs.map((tab) => {
                const count = getTabCount(tab.id);
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 ${
                      activeTab === tab.id
                        ? "border-b-2 border-[#6bbbe3] text-[#6bbbe3]"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {tab.name}
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-medium bg-[#ECF3FE] rounded-full">
                      {count > 99 ? "99+" : count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notifications Tab Content */}
          {activeTab === "allNotifications" && (
            <>
              {newNotifications.length > 0 && (
                <div className="flex w-full flex-col">
                  <NotificationCard
                    closeNotificationModal={closeNotificationModal}
                    title={t("New")}
                    notificationsList={newNotifications}
                    dismissNotification={dismissNotification}
                    hideActionCards
                    {...props}
                  />
                </div>
              )}

              {previousNotifications.length > 0 && (
                <div className="flex w-full flex-col">
                  <NotificationCard
                    closeNotificationModal={closeNotificationModal}
                    title={t("Earlier")}
                    notificationsList={previousNotifications}
                    dismissNotification={dismissNotification}
                    {...props}
                  />
                </div>
              )}

              {newNotifications.length === 0 && previousNotifications.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8">
                  <RiNotificationOffLine className="m-auto h-[6.25rem] w-[6.25rem] fill-[#a9dff9]" />
                  <p className="mt-2.5 text-center">{t("No Notifications right now!")}</p>
                </div>
              )}
            </>
          )}

          {/* Take Action Tab Content */}
          {activeTab === "takeAction" && (
            <div className="p-4">
              <ActionCards onCountChange={handleTakeActionCountChange} />
            </div>
          )}

          {/* Escrow Tab Content */}
          {/* {activeTab === "escrow" && (
            <div className="flex flex-col items-center justify-center py-8">
              <p className="text-center text-gray-500">{t("Escrow content coming soon")}</p>
            </div>
          )} */}

          {/* Support Tab Content */}
          {/* {activeTab === "support" && (
            <div className="flex flex-col items-center justify-center py-8">
              <p className="text-center text-gray-500">{t("Support content coming soon")}</p>
            </div>
          )} */}

          {/* Exported Files Tab Content */}
          {activeTab === "exportedFiles" && <ExportedFiles onCountChange={handleExportedFilesCountChange} />}
        </div>
      )}
    </>
  );
}

export default Notification;
